import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  LinearProgress,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  Delete,
  ArrowUpward,
  ArrowDownward,
  Translate as TranslateIcon,
  WorkspacePremium,
  Group,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { Category, CategoryFormData, TemplateImage, CategoryPlatform, CATEGORY_PLATFORM_LABELS } from '../../types';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { getImageUrl } from '../../utils/imageUrlHelper';

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  category: Category | null;
}

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  isActive: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ open, onClose, category }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    isActive: true,
    displayOrder: 0,
    imagesPerRow: 3,
    hiddenPlatforms: [],
    icon: '',
  });
  const [nameValues, setNameValues] = useState<Record<string, string>>({});
  const [descriptionValues, setDescriptionValues] = useState<Record<string, string>>({});
  const [templateImages, setTemplateImages] = useState<TemplateImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ file: File; progress: number }[]>([]);

  // Fetch languages
  const { data: languages, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await apiService.getLanguages();
      return response.data as Language[];
    }
  });

  useEffect(() => {
    if (category) {
      // Handle multilingual name and description
      // Check if name is a string (legacy) or object (multilingual)
      const nameObj = typeof category.name === 'string' 
        ? { EN: category.name } 
        : (category.name || {});
      
      const descObj = typeof category.description === 'string' 
        ? { EN: category.description || '' } 
        : (category.description || {});

      // Initialize name and description values for all active languages
      const activeLanguages = languages?.filter(l => l.isActive) || [];
      const initialNameValues: Record<string, string> = {};
      const initialDescValues: Record<string, string> = {};
      
      activeLanguages.forEach(lang => {
        initialNameValues[lang.code] = nameObj[lang.code] || nameObj[lang.code.toUpperCase()] || '';
        initialDescValues[lang.code] = descObj[lang.code] || descObj[lang.code.toUpperCase()] || '';
      });
      
      setNameValues(initialNameValues);
      setDescriptionValues(initialDescValues);
      
      setFormData({
        name: '', // Not used anymore, kept for backward compatibility
        description: '', // Not used anymore, kept for backward compatibility
        isActive: category.isActive,
        displayOrder: category.displayOrder || 0,
        imagesPerRow: category.imagesPerRow || 3,
        hiddenPlatforms: category.hiddenPlatforms || [],
        icon: category.icon || '',
      });
      // Sort template images by order
      const sortedImages = [...(category.templateImages || [])].sort((a, b) => a.order - b.order);
      setTemplateImages(sortedImages);
    } else {
      // Initialize empty values for all active languages
      const activeLanguages = languages?.filter(l => l.isActive) || [];
      const initialNameValues: Record<string, string> = {};
      const initialDescValues: Record<string, string> = {};
      
      activeLanguages.forEach(lang => {
        initialNameValues[lang.code] = '';
        initialDescValues[lang.code] = '';
      });
      
      setNameValues(initialNameValues);
      setDescriptionValues(initialDescValues);
      
      setFormData({
        name: '',
        description: '',
        isActive: true,
        displayOrder: 0,
        imagesPerRow: 3,
        hiddenPlatforms: [],
        icon: '',
      });
      setTemplateImages([]);
    }
  }, [category, open, languages]);

  const handleSubmit = async () => {
    // Validate that at least one language has a name
    const hasName = Object.values(nameValues).some(val => val && val.trim());
    if (!hasName) {
      toast.error('Category name is required in at least one language');
      return;
    }

    try {
      setLoading(true);
      
      // Build multilingual name and description objects
      const nameObj: Record<string, string> = {};
      const descObj: Record<string, string> = {};
      
      Object.keys(nameValues).forEach(code => {
        const value = nameValues[code]?.trim();
        if (value) {
          nameObj[code.toUpperCase()] = value;
        }
      });
      
      Object.keys(descriptionValues).forEach(code => {
        const value = descriptionValues[code]?.trim();
        if (value) {
          descObj[code.toUpperCase()] = value;
        }
      });

      // Prepare form data with multilingual format
      const submitData = {
        ...formData,
        name: nameObj,
        description: descObj,
      };

      let result;

      if (category) {
        // Update existing category
        result = await apiService.updateCategory(category._id, submitData);
        if (result.success) {
          toast.success('Category updated successfully');
          
          // If we have new template images (temp IDs), add them to the category
          // Sort by order before adding
          const newImages = templateImages
            .filter(img => img._id && img._id.startsWith('temp_'))
            .sort((a, b) => a.order - b.order);
          
          console.log('New images to add:', newImages.length, newImages);
          console.log('All template images:', templateImages.length, templateImages);
          
          if (newImages.length > 0) {
            try {
              // Use batch add method to add all images at once
              const imagesData = newImages.map(image => ({
                imageUrl: image.imageUrl,
                imageName: image.imageName,
                fileType: image.fileType || 'image',
                mimeType: image.mimeType || '',
                fileSize: image.fileSize || 0,
                order: image.order,
                isPremium: image.isPremium || false,
                isGroupImage: image.isGroupImage || false,
              }));

              console.log('Sending batch request with', imagesData.length, 'images:', imagesData);
              const result = await apiService.addMultipleTemplateImages(category._id, imagesData);
              if (result.success) {
                toast.success(`${newImages.length} template image(s) added successfully`);
                // Refresh the category data to show the new images
                // The parent component should handle refreshing the category list
              } else {
                console.error('Failed to add template images:', result);
                toast.error(result.error || result.details || `Failed to add ${newImages.length} template image(s)`);
              }
            } catch (err: any) {
              console.error('Error adding template images:', err);
              toast.error(err.response?.data?.error || err.response?.data?.details || err.message || `Failed to add ${newImages.length} template image(s)`);
            }
          }
          
          // Update existing images (order and premium status)
          const savedImages = templateImages.filter(img => !img._id.startsWith('temp_'));
          if (savedImages.length > 0) {
            // Update order
            const imageOrders = savedImages.map(img => ({ imageId: img._id, order: img.order }));
            await apiService.reorderTemplateImages(category._id, imageOrders);
            
            // Update premium status for existing images if changed
            // Get current category to compare premium status
            const categoryResult = await apiService.getCategory(category._id);
            if (categoryResult.success && categoryResult.data) {
              const currentCategory = categoryResult.data;
              
              // Update premium status and group image status for each existing image if changed
              for (const savedImage of savedImages) {
                const existingImage = currentCategory.templateImages?.find(
                  (img: TemplateImage) => img._id === savedImage._id
                );
                if (existingImage) {
                  // Update premium status if changed
                  if (existingImage.isPremium !== (savedImage.isPremium || false)) {
                    await apiService.updateTemplateImagePremium(
                      category._id,
                      savedImage._id,
                      savedImage.isPremium || false
                    );
                  }
                  // Update group image status if changed
                  if (existingImage.isGroupImage !== (savedImage.isGroupImage || false)) {
                    await apiService.updateTemplateImageGroupImage(
                      category._id,
                      savedImage._id,
                      savedImage.isGroupImage || false
                    );
                  }
                }
              }
            }
          }
        } else {
          toast.error(result.error || 'Failed to update category');
          return;
        }
      } else {
        // Create new category
        result = await apiService.createCategory(submitData);
        if (result.success && result.data) {
          toast.success('Category created successfully');
          // If we have template images, add them (sorted by order)
          if (templateImages.length > 0 && result.data._id) {
            const sortedImages = [...templateImages].sort((a, b) => a.order - b.order);
            try {
              // Use batch add method to add all images at once
              const imagesData = sortedImages.map(image => ({
                imageUrl: image.imageUrl,
                imageName: image.imageName,
                fileType: image.fileType || 'image',
                mimeType: image.mimeType || '',
                fileSize: image.fileSize || 0,
                order: image.order,
                isPremium: image.isPremium || false,
                isGroupImage: image.isGroupImage || false,
              }));

              const imageResult = await apiService.addMultipleTemplateImages(result.data._id, imagesData);
              if (imageResult.success) {
                toast.success(`${sortedImages.length} template image(s) added successfully`);
              } else {
                console.error('Failed to add template images:', imageResult);
                toast.error(imageResult.error || imageResult.details || `Failed to add ${sortedImages.length} template image(s)`);
              }
            } catch (err: any) {
              console.error('Error adding template images:', err);
              toast.error(err.response?.data?.error || err.response?.data?.details || err.message || `Failed to add ${sortedImages.length} template image(s)`);
            }
          }
        } else {
          toast.error(result.error || 'Failed to create category');
          return;
        }
      }

      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 
                          'video/mp4', 'video/mpeg', 'video/webm'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.mp4', '.mpeg', '.webm'];
    
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
      toast.error(`Invalid file type for "${file.name}". Allowed: images, videos, GIFs`);
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File "${file.name}" is too large. Maximum size is 50MB`);
      return;
    }

    try {
      // Upload file
      const uploadResult = await apiService.uploadTemplateImage(file);
      
      if (uploadResult.success && uploadResult.data) {
        const newImage: TemplateImage = {
          _id: `temp_${Date.now()}_${Math.random()}`,
          imageUrl: uploadResult.data.url,
          imageName: file.name,
          fileType: uploadResult.data.type as 'image' | 'video' | 'gif',
          mimeType: uploadResult.data.mimetype,
          fileSize: uploadResult.data.size,
          order: 0, // Will be set based on current length
          isPremium: false,
          isGroupImage: false,
          uploadedAt: new Date().toISOString(),
        };

        // Use functional update to ensure we have the latest state
        setTemplateImages(prevImages => {
          const updatedOrder = prevImages.length;
          return [...prevImages, { ...newImage, order: updatedOrder }];
        });
        return { success: true, file: file.name };
      } else {
        return { success: false, file: file.name, error: uploadResult.error || 'Failed to upload file' };
      }
    } catch (err: any) {
      return { success: false, file: file.name, error: err.message || 'Failed to upload file' };
    }
  };

  const handleMultipleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Validate all files first
    const invalidFiles: string[] = [];
    const validFiles: File[] = [];

    fileArray.forEach(file => {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 
                            'video/mp4', 'video/mpeg', 'video/webm'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.mp4', '.mpeg', '.webm'];
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
        invalidFiles.push(file.name);
      } else if (file.size > 50 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (too large)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid files: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length === 0) {
      return;
    }

    setUploading(true);
    setUploadingFiles(validFiles.map(f => ({ file: f, progress: 0 })));

    const uploadedImages: TemplateImage[] = [];
    let successCount = 0;

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadingFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, progress: 50 } : f
      ));

      try {
        // Upload file
        const uploadResult = await apiService.uploadTemplateImage(file);
        
        if (uploadResult.success && uploadResult.data) {
          const newImage: TemplateImage = {
            _id: `temp_${Date.now()}_${Math.random()}`,
            imageUrl: uploadResult.data.url,
            imageName: file.name,
            fileType: uploadResult.data.type as 'image' | 'video' | 'gif',
            mimeType: uploadResult.data.mimetype,
            fileSize: uploadResult.data.size,
            order: 0, // Will be set when adding to state
            isPremium: false,
            isGroupImage: false,
            uploadedAt: new Date().toISOString(),
          };
          uploadedImages.push(newImage);
          successCount++;
        } else {
          toast.error(`Failed to upload ${file.name}: ${uploadResult.error || 'Unknown error'}`);
        }
      } catch (err: any) {
        toast.error(`Failed to upload ${file.name}: ${err.message || 'Unknown error'}`);
      }

      setUploadingFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, progress: 100 } : f
      ));
    }

    // Add all uploaded images to state at once
    if (uploadedImages.length > 0) {
      setTemplateImages(prevImages => {
        const currentLength = prevImages.length;
        const imagesWithOrder = uploadedImages.map((img, index) => ({
          ...img,
          order: currentLength + index
        }));
        return [...prevImages, ...imagesWithOrder];
      });
    }

    setUploadingFiles([]);
    setUploading(false);

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)`);
    }
  };


  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultipleFileUpload(files);
      // Reset input to allow selecting the same files again
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (imageId: string) => {
    const imageToDelete = templateImages.find(img => img._id === imageId);
    const imageName = imageToDelete?.imageName || 'this image';
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${imageName}"? This will also remove the file from storage.`)) {
      return;
    }

    if (category && !imageId.startsWith('temp_')) {
      // Remove from server (this will also delete the file from storage)
      try {
        const result = await apiService.removeTemplateImage(category._id, imageId);
        if (result.success) {
          toast.success('Image removed successfully');
          const updatedImages = templateImages.filter(img => img._id !== imageId);
          // Reorder remaining images
          const reorderedImages = updatedImages.map((img, index) => ({
            ...img,
            order: index,
          }));
          setTemplateImages(reorderedImages);
        } else {
          toast.error(result.error || result.details || 'Failed to remove image');
        }
      } catch (err: any) {
        toast.error(err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to remove image');
      }
    } else {
      // Remove from local state (not yet saved)
      toast.success('Image removed from list');
      const updatedImages = templateImages.filter(img => img._id !== imageId);
      // Reorder remaining images
      const reorderedImages = updatedImages.map((img, index) => ({
        ...img,
        order: index,
      }));
      setTemplateImages(reorderedImages);
    }
  };

  const handleTranslate = async (translateType: 'name' | 'description' | 'both' = 'both') => {
    // Find English language from languages array
    const englishLang = languages?.find(l => 
      l.code.toUpperCase() === 'EN' || 
      l.code.toLowerCase() === 'en' ||
      l.name.toLowerCase() === 'english'
    );
    
    const englishLangCode = englishLang?.code || 'en';
    const englishLangCodeUpper = englishLangCode.toUpperCase();
    const englishLangCodeLower = englishLangCode.toLowerCase();
    
    // Get English content - check all possible formats
    const englishName = nameValues[englishLangCode] ?? 
                        nameValues[englishLangCodeUpper] ?? 
                        nameValues[englishLangCodeLower] ?? 
                        nameValues['EN'] ?? 
                        nameValues['en'] ?? '';
    
    const englishDescription = descriptionValues[englishLangCode] ?? 
                              descriptionValues[englishLangCodeUpper] ?? 
                              descriptionValues[englishLangCodeLower] ?? 
                              descriptionValues['EN'] ?? 
                              descriptionValues['en'] ?? '';

    console.log('English language code:', englishLangCode);
    console.log('Name values keys:', Object.keys(nameValues));
    console.log('Description values keys:', Object.keys(descriptionValues));
    console.log('English name found:', englishName ? `${englishName.substring(0, 50)}...` : 'NOT FOUND');
    console.log('English description found:', englishDescription ? `${englishDescription.substring(0, 50)}...` : 'NOT FOUND');

    if (translateType === 'name' && !englishName) {
      toast.error('Please provide English name to translate');
      return;
    }

    if (translateType === 'description' && !englishDescription) {
      toast.error('Please provide English description to translate');
      return;
    }

    if (translateType === 'both' && !englishName && !englishDescription) {
      toast.error('Please provide English name or description to translate');
      return;
    }

    setIsTranslating(true);

    try {
      const activeLanguages = languages?.filter(l => {
        const code = l.code.toUpperCase();
        return l.isActive && code !== 'EN' && code !== englishLangCodeUpper;
      }) || [];
      
      if (activeLanguages.length === 0) {
        toast.error('No active languages found to translate to');
        return;
      }
      
      console.log(`Starting translation: type=${translateType}, languages=${activeLanguages.length}`);
      
      // Initialize final values with current state
      let finalNameValues = { ...nameValues };
      let finalDescriptionValues = { ...descriptionValues };
      
      let translatedNameCount = 0;
      let translatedDescriptionCount = 0;
      
      // Translate to all active languages except English
      for (const lang of activeLanguages) {
        const langCode = lang.code.toUpperCase();
        const langCodeLower = lang.code.toLowerCase();

        // Translate name if needed
        if (translateType === 'name' || translateType === 'both') {
          const currentName = finalNameValues[langCode] || finalNameValues[langCodeLower] || '';
          if (englishName && (!currentName || currentName.trim() === '')) {
            try {
              console.log(`[API CALL] Translating name to ${langCode}...`);
              const nameResponse = await apiService.translateText(englishName, langCode, 'en');
              console.log(`[API RESPONSE] Name translation for ${langCode}:`, nameResponse);
              
              if (nameResponse.success && nameResponse.data?.translatedText) {
                finalNameValues[langCode] = nameResponse.data.translatedText;
                translatedNameCount++;
                console.log(`✓ Name translated to ${langCode}:`, nameResponse.data.translatedText);
              } else {
                console.warn(`✗ Name translation failed for ${langCode}:`, nameResponse);
                toast.error(`Failed to translate name to ${lang.name}`);
              }
            } catch (error: any) {
              console.error(`✗ Exception translating name to ${langCode}:`, error);
              toast.error(`Failed to translate name to ${lang.name}: ${error.message || 'Unknown error'}`);
            }
          } else {
            console.log(`Skipping name translation for ${langCode} (already has value or no English name)`);
          }
        }

        // Translate description if needed
        if (translateType === 'description' || translateType === 'both') {
          const currentDescription = finalDescriptionValues[langCode] || finalDescriptionValues[langCodeLower] || '';
          if (englishDescription && (!currentDescription || currentDescription.trim() === '')) {
            try {
              if (englishDescription.trim()) {
                console.log(`[API CALL] Translating description to ${langCode}...`);
                const descResponse = await apiService.translateText(englishDescription, langCode, 'en');
                console.log(`[API RESPONSE] Description translation for ${langCode}:`, {
                  success: descResponse.success,
                  hasData: !!descResponse.data,
                  textLength: descResponse.data?.translatedText?.length || 0
                });
                
                if (descResponse.success && descResponse.data?.translatedText) {
                  finalDescriptionValues[langCode] = descResponse.data.translatedText;
                  translatedDescriptionCount++;
                  console.log(`✓ Description translated to ${langCode}, length:`, descResponse.data.translatedText.length);
                } else {
                  console.warn(`✗ Description translation failed for ${langCode}:`, descResponse);
                  toast.error(`Failed to translate description to ${lang.name}`);
                }
              }
            } catch (error: any) {
              console.error(`✗ Exception translating description to ${langCode}:`, error);
              console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
              });
              toast.error(`Failed to translate description to ${lang.name}: ${error.message || 'Unknown error'}`);
            }
          } else {
            console.log(`Skipping description translation for ${langCode} (already has value or no English description)`);
          }
        }
      }

      console.log(`Translation complete: ${translatedNameCount} names, ${translatedDescriptionCount} descriptions translated`);

      setNameValues(finalNameValues);
      setDescriptionValues(finalDescriptionValues);
      
      // Only show success if translations actually happened
      if (translatedNameCount > 0 || translatedDescriptionCount > 0) {
        const parts = [];
        if (translatedNameCount > 0) parts.push(`${translatedNameCount} name(s)`);
        if (translatedDescriptionCount > 0) parts.push(`${translatedDescriptionCount} description(s)`);
        toast.success(`Translated ${parts.join(' and ')} successfully`);
      } else {
        toast.error('No translations were made. All languages may already have content, or English content is missing.');
      }
    } catch (error: any) {
      console.error('Translation error:', error);
      toast.error(error.response?.data?.message || 'Failed to translate content. Please check if translation API is configured.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTogglePremium = (imageId: string) => {
    const updatedImages = templateImages.map(img => 
      img._id === imageId 
        ? { ...img, isPremium: !img.isPremium }
        : img
    );
    setTemplateImages(updatedImages);
  };

  const handleToggleGroupImage = (imageId: string) => {
    const updatedImages = templateImages.map(img => 
      img._id === imageId 
        ? { ...img, isGroupImage: !img.isGroupImage }
        : img
    );
    setTemplateImages(updatedImages);
  };

  const handleMoveImage = async (imageId: string, direction: 'up' | 'down') => {
    const currentIndex = templateImages.findIndex(img => img._id === imageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= templateImages.length) return;

    // Create new array with swapped items
    const newImages = [...templateImages];
    [newImages[currentIndex], newImages[newIndex]] = [newImages[newIndex], newImages[currentIndex]];

    // Update order values
    const reorderedImages = newImages.map((img, index) => ({
      ...img,
      order: index,
    }));

    setTemplateImages(reorderedImages);

    // If category exists and image is saved (not temp), update order on server
    if (category && !imageId.startsWith('temp_')) {
      try {
        const imageOrders = reorderedImages
          .filter(img => !img._id.startsWith('temp_'))
          .map(img => ({ imageId: img._id, order: img.order }));
        
        if (imageOrders.length > 0) {
          const result = await apiService.reorderTemplateImages(category._id, imageOrders);
          if (result.success) {
            toast.success('Image order updated');
          } else {
            toast.error(result.error || 'Failed to update image order');
            // Revert on error
            const sortedImages = [...(category.templateImages || [])].sort((a, b) => a.order - b.order);
            setTemplateImages(sortedImages);
          }
        }
      } catch (err: any) {
        toast.error(err.message || 'Failed to update image order');
        // Revert on error
        if (category) {
          const sortedImages = [...(category.templateImages || [])].sort((a, b) => a.order - b.order);
          setTemplateImages(sortedImages);
        }
      }
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {category ? 'Edit Category' : 'Create Category'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* Multilingual Name Fields */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Category Name *
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enter category name in different languages
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TranslateIcon />}
                  onClick={() => handleTranslate('name')}
                  disabled={isTranslating || (!nameValues['EN'] && !nameValues['en'])}
                >
                  {isTranslating ? 'Translating...' : 'Auto Translate'}
                </Button>
              </Box>
              {languagesLoading ? (
                <CircularProgress size={20} />
              ) : (
                languages?.filter(l => l.isActive).map((lang) => (
                  <TextField
                    key={lang.code}
                    fullWidth
                    label={`Name (${lang.name} - ${lang.nativeName})`}
                    value={nameValues[lang.code] || ''}
                    onChange={(e) => {
                      setNameValues({
                        ...nameValues,
                        [lang.code]: e.target.value
                      });
                    }}
                    required={lang.code === 'EN' || lang.code === 'en'} // English is required
                    sx={{ mb: 2 }}
                    helperText={lang.code === 'EN' || lang.code === 'en' ? 'Required' : 'Optional'}
                  />
                ))
              )}
            </Grid>
            
            {/* Multilingual Description Fields */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Optional description for the category
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TranslateIcon />}
                  onClick={() => handleTranslate('description')}
                  disabled={isTranslating || (!descriptionValues['EN'] && !descriptionValues['en'])}
                >
                  {isTranslating ? 'Translating...' : 'Auto Translate'}
                </Button>
              </Box>
              {languagesLoading ? (
                <CircularProgress size={20} />
              ) : (
                languages?.filter(l => l.isActive).map((lang) => (
                  <TextField
                    key={lang.code}
                    fullWidth
                    label={`Description (${lang.name} - ${lang.nativeName})`}
                    value={descriptionValues[lang.code] || ''}
                    onChange={(e) => {
                      setDescriptionValues({
                        ...descriptionValues,
                        [lang.code]: e.target.value
                      });
                    }}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                ))
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                helperText="Lower numbers appear first"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Images Per Row</InputLabel>
                <Select
                  value={formData.imagesPerRow || 3}
                  label="Images Per Row"
                  onChange={(e) => setFormData({ ...formData, imagesPerRow: e.target.value as number })}
                >
                  <MenuItem value={3}>3 Images</MenuItem>
                  <MenuItem value={4}>4 Images</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Number of template images to display per row in the app
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Category Image</Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: formData.icon ? 'transparent' : 'action.hover',
                  minHeight: '150px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'action.hover',
                  },
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    try {
                      setLoading(true);
                      const uploadResult = await apiService.uploadCategoryImage(file);
                      if (uploadResult.success && uploadResult.data) {
                        setFormData({ ...formData, icon: uploadResult.data.url });
                        toast.success('Category image uploaded successfully');
                      } else {
                        toast.error(uploadResult.error || 'Failed to upload image');
                      }
                    } catch (err: any) {
                      toast.error(err.message || 'Failed to upload image');
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              >
                <input
                  type="file"
                  id="category-image-upload"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        setLoading(true);
                        const uploadResult = await apiService.uploadCategoryImage(file);
                        if (uploadResult.success && uploadResult.data) {
                          setFormData({ ...formData, icon: uploadResult.data.url });
                          toast.success('Category image uploaded successfully');
                        } else {
                          toast.error(uploadResult.error || 'Failed to upload image');
                        }
                      } catch (err: any) {
                        toast.error(err.message || 'Failed to upload image');
                      } finally {
                        setLoading(false);
                        e.target.value = '';
                      }
                    }
                  }}
                  disabled={loading}
                />
                {formData.icon ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img 
                      src={getImageUrl(formData.icon)} 
                      alt="Category image" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, icon: '' });
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <label htmlFor="category-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Add />}
                        disabled={loading}
                        sx={{ mb: 1 }}
                      >
                        {loading ? 'Uploading...' : 'Upload Category Image'}
                      </Button>
                    </label>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Drag & drop image here, or click to select
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supports: Images (JPG, PNG, WebP) - Max 50MB
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Show on platforms
              </Typography>
              <FormGroup row>
                {(Object.keys(CATEGORY_PLATFORM_LABELS) as CategoryPlatform[]).map((platform) => {
                  const hidden = (formData.hiddenPlatforms || []).includes(platform);
                  return (
                    <FormControlLabel
                      key={platform}
                      control={
                        <Checkbox
                          checked={!hidden}
                          onChange={(e) => {
                            const current = formData.hiddenPlatforms || [];
                            setFormData({
                              ...formData,
                              hiddenPlatforms: e.target.checked
                                ? current.filter((p) => p !== platform)
                                : [...current, platform],
                            });
                          }}
                        />
                      }
                      label={CATEGORY_PLATFORM_LABELS[platform]}
                    />
                  );
                })}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Untick a platform to hide this category there. App builds older than Jan 2026 don't
                send their platform, so they still see every active category.
              </Typography>
            </Grid>

            {/* Template Images Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Template Images
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload multiple template images or videos at once. You can mark them as Premium or Group images.
                </Typography>
                
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 3, 
                    mb: 3,
                    border: '2px dashed',
                    borderColor: uploading ? 'primary.main' : 'divider',
                    backgroundColor: uploading ? 'action.hover' : 'background.paper',
                    transition: 'all 0.3s ease',
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = 'primary.main';
                    e.currentTarget.style.backgroundColor = 'action.hover';
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = 'divider';
                    e.currentTarget.style.backgroundColor = 'background.paper';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = 'divider';
                    e.currentTarget.style.backgroundColor = 'background.paper';
                    const files = e.dataTransfer.files;
                    if (files && files.length > 0) {
                      handleMultipleFileUpload(files);
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <input
                      type="file"
                      id="template-image-upload"
                      accept="image/*,video/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                      disabled={uploading}
                    />
                    <label htmlFor="template-image-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<Add />}
                        disabled={uploading}
                        size="large"
                        sx={{ mb: 2, minWidth: 200 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload Files'}
                      </Button>
                    </label>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                      Drag & drop multiple files here, or click to select
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supports: Images (JPG, PNG, WebP, GIF), Videos (MP4, WebM) - Max 50MB per file
                    </Typography>
                    
                    {uploadingFiles.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Uploading {uploadingFiles.length} file(s)...
                        </Typography>
                        {uploadingFiles.map((uploadFile, idx) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" noWrap sx={{ flex: 1, textAlign: 'left', mr: 1 }}>
                                {uploadFile.file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {uploadFile.progress}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={uploadFile.progress} 
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {uploading && uploadingFiles.length === 0 && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress variant="indeterminate" sx={{ height: 6, borderRadius: 3 }} />
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>

              {templateImages.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {templateImages.length} Template Image{templateImages.length !== 1 ? 's' : ''}
                    </Typography>
                    <Chip 
                      label={`Total: ${templateImages.length}`} 
                      color="primary" 
                      size="small"
                    />
                  </Box>
                  <Grid container spacing={2}>
                    {[...templateImages]
                      .sort((a, b) => a.order - b.order)
                      .map((image, index) => {
                        const sortedImages = [...templateImages].sort((a, b) => a.order - b.order);
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={image._id}>
                            <Card 
                              elevation={2}
                              sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 4,
                                },
                              }}
                            >
                            {image.fileType === 'video' ? (
                              <CardMedia
                                component="video"
                                height="140"
                                src={getImageUrl(image.imageUrl)}
                                controls
                                sx={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <CardMedia
                                component="img"
                                height="240"
                                image={getImageUrl(image.imageUrl)}
                                alt={image.imageName}
                                sx={{ objectFit: 'contain' }}
                                onError={(e: any) => {
                                  e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                                }}
                              />
                            )}
                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                              <Typography 
                                variant="subtitle2" 
                                noWrap
                                sx={{ 
                                  fontWeight: 600,
                                  mb: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={image.imageName}
                              >
                                {image.imageName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                {image.fileType && (
                                  <Chip 
                                    label={image.fileType.toUpperCase()} 
                                    size="small" 
                                    color="primary"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                                {image.isPremium && (
                                  <Chip 
                                    icon={<WorkspacePremium sx={{ fontSize: 14 }} />}
                                    label="Premium" 
                                    size="small" 
                                    color="warning"
                                    sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                                {image.isGroupImage && (
                                  <Chip 
                                    icon={<Group sx={{ fontSize: 14 }} />}
                                    label="Group" 
                                    size="small" 
                                    color="info"
                                    sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Order: {image.order + 1}
                              </Typography>
                            </CardContent>
                            <CardActions 
                              sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                flexWrap: 'wrap', 
                                gap: 1,
                                pt: 1,
                                px: 1.5,
                                pb: 1.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                minHeight: '48px',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMoveImage(image._id, 'up')}
                                  disabled={index === 0}
                                  title="Move up"
                                  sx={{ 
                                    '&:disabled': { opacity: 0.3 },
                                  }}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMoveImage(image._id, 'down')}
                                  disabled={index === sortedImages.length - 1}
                                  title="Move down"
                                  sx={{ 
                                    '&:disabled': { opacity: 0.3 },
                                  }}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={image.isPremium || false}
                                      onChange={() => handleTogglePremium(image._id)}
                                      color="warning"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <WorkspacePremium fontSize="small" />
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                        Premium
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ m: 0 }}
                                />
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={image.isGroupImage || false}
                                      onChange={() => handleToggleGroupImage(image._id)}
                                      color="info"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Group fontSize="small" />
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                        Group
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveImage(image._id)}
                                  title="Delete image"
                                  sx={{ 
                                    ml: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'error.dark',
                                    }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
                </Box>
              )}

            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : category ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryForm;

